import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Badge } from "@/components/ui/Badge";
import {
  listInviteCodes,
  createInviteCode,
  listRedemptions,
} from "@/features/billing/api/billing";
import type { InviteCode, InviteRedemption } from "@/features/billing/api/schemas";
import { ApiError } from "@/api/fetch";
import { EditExpiryDialog } from "@/features/admin/components/EditExpiryDialog";
import { Check, Copy, Pencil, Plus, Users } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const CreateCodeForm = ({ onCreated }: { onCreated: () => void }) => {
  const [customCode, setCustomCode] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const body: {
        plan_id?: string;
        max_uses?: number | null;
        expires_at?: string;
        custom_code?: string;
      } = { plan_id: "beta_free_unlimited" };
      if (customCode.trim()) body.custom_code = customCode.trim().toUpperCase();
      if (maxUses.trim()) body.max_uses = parseInt(maxUses, 10);
      if (expiresAt.trim()) {
        const d = new Date(expiresAt);
        if (Number.isNaN(d.getTime()) || d.getTime() <= Date.now()) {
          toast.error("Expiration must be a date in the future");
          return;
        }
        body.expires_at = d.toISOString();
      }
      setSubmitting(true);
      try {
        const result = await createInviteCode(body);
        toast.success(`Created code: ${result.code}`);
        setCustomCode("");
        setMaxUses("");
        setExpiresAt("");
        onCreated();
      } catch (err) {
        const msg =
          err instanceof ApiError ? err.message : "Failed to create code";
        toast.error(msg);
      } finally {
        setSubmitting(false);
      }
    },
    [customCode, maxUses, expiresAt, onCreated],
  );

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-4 rounded-xl border border-zinc-200 p-4"
    >
      <h2 className="text-sm font-semibold text-zinc-900">Create invite code</h2>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="custom-code"
            className="block text-xs font-medium text-zinc-600 mb-1"
          >
            Code (optional — auto-generated if empty)
          </label>
          <Input
            id="custom-code"
            value={customCode}
            onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
            placeholder="HACKATHON2026"
            className="font-mono"
          />
        </div>
        <div>
          <label
            htmlFor="max-uses"
            className="block text-xs font-medium text-zinc-600 mb-1"
          >
            Max uses (optional — blank = unlimited)
          </label>
          <Input
            id="max-uses"
            type="number"
            min="1"
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="200"
          />
        </div>
        <div>
          <label
            htmlFor="expires-at"
            className="block text-xs font-medium text-zinc-600 mb-1"
          >
            Expires at (optional — blank = never)
          </label>
          <Input
            id="expires-at"
            type="datetime-local"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
          />
        </div>
      </div>
      <Button type="submit" loading={submitting} disabled={submitting}>
        <Plus className="w-4 h-4" />
        Create code
      </Button>
    </form>
  );
};

const RedemptionsList = ({ code }: { code: string }) => {
  const [redemptions, setRedemptions] = useState<InviteRedemption[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    listRedemptions(code)
      .then((data) => {
        if (!cancelled) setRedemptions(data);
      })
      .catch(() => {
        if (!cancelled) setRedemptions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [code]);

  if (loading) {
    return (
      <div className="px-4 py-3 text-xs text-zinc-400">Loading redemptions…</div>
    );
  }

  if (!redemptions || redemptions.length === 0) {
    return (
      <div className="px-4 py-3 text-xs text-zinc-400">
        No redemptions yet.
      </div>
    );
  }

  return (
    <div className="border-t border-zinc-100">
      <table className="w-full text-xs">
        <thead className="text-left text-zinc-400">
          <tr>
            <th className="px-4 py-1.5 font-medium">Email</th>
            <th className="px-4 py-1.5 font-medium">Name</th>
            <th className="px-4 py-1.5 font-medium">Redeemed at</th>
          </tr>
        </thead>
        <tbody className="text-zinc-700">
          {redemptions.map((r) => (
            <tr key={r.userId} className="border-t border-zinc-50">
              <td className="px-4 py-1.5">{r.email}</td>
              <td className="px-4 py-1.5">{r.fullName || "—"}</td>
              <td className="px-4 py-1.5">
                {r.redeemedAt
                  ? new Date(r.redeemedAt).toLocaleString()
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CodeRow = ({
  code,
  onUpdated,
}: {
  code: InviteCode;
  onUpdated: () => void;
}) => {
  const [expanded, setExpanded] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const redeemUrl = code.redeemUrl
    || `https://paperclip.gxl.ai/redeem?code=${code.code}`;
  const isExpired =
    code.expiresAt != null && new Date(code.expiresAt).getTime() < Date.now();

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(redeemUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [redeemUrl]);

  return (
    <div className="rounded-xl border border-zinc-200">
      <div className="flex items-center justify-between px-4 py-3">
        <div className="flex items-center gap-3">
          <code className="text-sm font-semibold text-zinc-900">{code.code}</code>
          <span className="text-xs text-zinc-400">{code.planId}</span>
          {code.maxUses != null && (
            <span className="text-xs text-zinc-500">
              {code.usedCount}/{code.maxUses} used
            </span>
          )}
          {code.maxUses == null && (
            <span className="text-xs text-zinc-500">
              {code.usedCount} used
            </span>
          )}
          {isExpired && (
            <Badge variant="red">Expired</Badge>
          )}
          {code.expiresAt && (
            <span className="text-xs text-zinc-400">
              expires {new Date(code.expiresAt).toLocaleDateString()}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden />
            Expiry
          </button>
          <button
            type="button"
            onClick={handleCopy}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Copy redeem URL"
          >
            {copied ? (
              <Check className="h-3.5 w-3.5" aria-hidden />
            ) : (
              <Copy className="h-3.5 w-3.5" aria-hidden />
            )}
            {copied ? "Copied" : "Copy URL"}
          </button>
          <button
            type="button"
            aria-expanded={expanded}
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          >
            <Users className="h-3.5 w-3.5" aria-hidden />
            Redemptions
          </button>
        </div>
      </div>
      {expanded && <RedemptionsList code={code.code} />}
      <EditExpiryDialog
        code={code.code}
        expiresAt={code.expiresAt}
        open={editOpen}
        onOpenChange={setEditOpen}
        onUpdated={onUpdated}
      />
    </div>
  );
};

export const AdminPage = () => {
  useDocumentTitle("Admin");
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(() => {
    setLoading(true);
    listInviteCodes()
      .then(setCodes)
      .catch(() => {
        toast.error("Failed to load invite codes");
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="h-full overflow-y-auto bg-white">
      <div className="mx-auto max-w-3xl px-6 py-12 space-y-6">
        <header>
          <h1 className="font-display text-4xl leading-none tracking-tight text-zinc-900">
            Admin
          </h1>
          <p className="mt-3 max-w-2xl text-base leading-7 text-zinc-600">
            Manage invite codes and user access.
          </p>
        </header>

        <CreateCodeForm onCreated={refresh} />

        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-zinc-900">
            Invite codes
          </h2>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-zinc-900 border-t-transparent" />
            </div>
          ) : codes.length === 0 ? (
            <div className="rounded-xl border border-dashed border-zinc-200 px-4 py-8 text-center text-sm text-zinc-400">
              No invite codes yet. Create one above.
            </div>
          ) : (
            codes.map((code) => (
              <CodeRow key={code.code} code={code} onUpdated={refresh} />
            ))
          )}
        </div>
      </div>
    </div>
  );
};